<template>
  <div
    ref="wrapRef"
    class="tm-dropdown-wrap"
    :aria-expanded="internalValue"
    :class="{
      'tm-dropdown-wrap--hidden': hideDropdownWrapper,
      'tm-dropdown-wrap--expanded': internalValue,
    }"
    :disabled="disable ? 'disabled' : null"
  >
    <slot
      :is-show="internalValue"
      :toggle="toggle"
    />

    <!-- QMenu teleports, v-test-id would not apply -->
    <q-menu
      ref="qMenu"
      v-model="internalValue"
      v-bind="attrs"
      :target="target"
      :max-height="maxHeight"
      :max-width="maxWidth"
      :class="[
        dropdownClassName,
        contentClass,
        { 'filter-dropdown': filterDropdown },
        { 'tm-dropdown--max-height-unset': maxHeightUnset },
        { small: size === 'small' },
      ]"
      :style="{ '--tm-dropdown-width': width }"
      :auto-close="autoClose"
      :no-parent-event="noParentEvent || disable"
      :persistent="!closeOnOutsideClick"
      :fit="fit"
      :no-focus="noFocus"
      :no-position-overflow="false"
      :[TestIdAttrName]="menuTestId"
      @before-show="beforeShow"
      @before-hide="$emit('before-hide')"
      @hide="onHide"
      @show="onShow"
    >
      <div
        ref="innerMenuRef"
        class="tm-dropdown__main"
        :class="[
          `tm-dropdown__main--border-radius-${borderRadius}`,
          `tm-dropdown__main--${scrollbarStyle}`,
          { 'tm-dropdown__main--strong-shadow': strongShadow },
          { 'tm-dropdown__main--centering-shadow': centeringShadow },
          { 'tm-dropdown__main--overflow-hidden': overflowHidden },
          { 'tm-dropdown__main--unset-background': backgroundUnset },
        ]"
        @keydown="onMenuKeydown"
      >
        <tm-button
          v-if="showClose"
          class="tm-dropdown__close"
          size="small"
          flat
          icon-only
          no-border
          @click="close"
        >
          <tm-icon
            name="close"
            size="small"
          />
        </tm-button>
        <div
          ref="menuResizeObserverRef"
          class="tm-dropdown__menu-wrapper"
        >
          <slot
            name="menu"
            v-bind="{ close, qMenu, updatePosition }"
          />
        </div>
      </div>
    </q-menu>
  </div>
</template>

<script lang="ts">
import {
  useResizeObserver,
  computed,
  defineComponent,
  nextTick,
  ref,
  watch,
  type PropType,
} from '@/composition/vue/compositionApi'
import type { FakeQMenu, ControlPosition } from '@/composition/tooltip/renderTooltip'
import { getTooltipAngleAttrs, useRenderTooltip } from '@/composition/tooltip/renderTooltip'
import TmButton from '@/components/shared/TmButton.vue'
import type { ScrollbarStyle, SizeProp } from '@/components/shared/types'
import { dropdownClassName, dropdownSizes } from '@/components/shared/types'
import TmIcon from '@/components/shared/TmIcon.vue'
import { useInsideDropdown, useDropdownCloseOnScrollContext, useOpenDropdownRegistry } from '@/composition/dropdown'
import { TestIdAttrName } from '@/composition/testing/types'
import { useScrollInject } from '@/composition/scroll'
import { getFirstFocusableElement, getFocusableElements } from '@/utils/getFocusableElements'

export default defineComponent({
  name: 'TmDropdown',
  components: { TmIcon, TmButton },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    closeOnContentClick: {
      type: Boolean,
      default: true,
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: true,
    },
    filterDropdown: {
      type: Boolean,
      default: false,
    },
    overflowHidden: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as SizeProp<'' | 'small'>,
      default: '',
    },
    maxHeight: {
      type: String,
      default: dropdownSizes.maxHeight.md,
    },
    maxWidth: {
      type: String,
    },
    maxHeightUnset: {
      type: Boolean,
    },
    width: {
      type: String,
    },
    contentClass: {
      type: String,
      default: '',
    },
    strongShadow: {
      type: Boolean,
    },
    centeringShadow: {
      type: Boolean,
    },
    showClose: {
      type: Boolean,
    },
    target: {
      type: HTMLElement,
    },
    offset: {
      type: Array as PropType<number[]>,
    },
    offsetWithAngle: {
      type: Boolean,
    },
    position: {
      type: String as PropType<ControlPosition>,
      default: 'bottom-right',
    },
    hideDropdownWrapper: {
      type: Boolean,
    },
    noParentEvent: {
      type: Boolean,
    },
    disable: {
      type: Boolean,
    },
    borderRadius: {
      type: String as PropType<'def' | 'md'>,
      default: 'def',
    },
    ignoreAutosize: {
      type: Boolean,
      default: false,
    },
    fit: {
      type: Boolean,
      default: true,
    },
    backgroundUnset: {
      type: Boolean,
    },
    scrollbarStyle: {
      type: String as PropType<ScrollbarStyle>,
      default: 'thumb-with-track-hover',
    },
    noFocus: {
      type: Boolean,
      default: true,
    },
    noFocusOnShow: {
      type: Boolean,
      default: false,
    },
    menuTestId: {
      type: String,
      default: 'tm-dropdown-menu',
    },
    closeOnScroll: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    'update:model-value': (_val: boolean) => true,
    show: () => true,
    hide: (_e?: Event) => true,
    'before-hide': () => true,
  },
  setup(props, context: any) {
    const qMenu = ref<FakeQMenu>()
    const wrapRef = ref()
    const innerMenuRef = ref()
    const menuResizeObserverRef = ref()
    const internalValue = ref(props.modelValue)
    const { show, hide, reposition } = useRenderTooltip('tm-dropdown')
    const angleAttrs = computed(() => getTooltipAngleAttrs('tm-dropdown', props.position, props.offsetWithAngle))

    const autoClose = ref(props.filterDropdown ? false : props.closeOnContentClick)

    if (!props.ignoreAutosize) {
      useInsideDropdown().provide(true)
    }

    // create reactive attributes without class
    const attrsWithoutClass = computed(() => {
      const clonedAttrs = { ...context.attrs }
      delete clonedAttrs.class
      return clonedAttrs
    })

    const attrs = computed(() => ({
      ...angleAttrs.value,
      ...attrsWithoutClass.value,
      offset: props.offset || angleAttrs.value.offset,
    }))

    watch(
      () => props.modelValue,
      (newValue) => {
        internalValue.value = newValue
      },
    )
    watch(
      () => internalValue.value,
      (newValue) => {
        context.emit('update:model-value', newValue)
      },
    )

    const { registerAndCloseUnrelated, unregister } = useOpenDropdownRegistry({
      contains: (el: Element) => Boolean(innerMenuRef.value?.contains(el) || wrapRef.value?.contains(el)),
      close: () => close(),
    })

    const beforeShow = async () => {
      const anchorEl = props.target || wrapRef.value
      registerAndCloseUnrelated(anchorEl)
      // wait for the menu DOM before positioning, so the angle renders correctly
      await nextTick()
      const menuEl = innerMenuRef.value?.parentElement

      context.emit('show')

      show({
        anchorEl,
        menuEl,
        attrs: attrs.value,
        qMenu,
        maxHeight: props.maxHeightUnset ? undefined : parseInt(props.maxHeight!, 10),
        fitWidth: props.fit && props.width ? props.width : undefined,
      })
    }

    // QMenu may emit hide event without an event if it was closed by v-model
    const onHide = (e?: Event) => {
      unregister()
      context.emit('hide', e)
      hide()
    }

    const close = () => {
      internalValue.value = false
    }

    const closeOnScrollFromContext = useDropdownCloseOnScrollContext().inject()
    useScrollInject(() => {
      if ((props.closeOnScroll || closeOnScrollFromContext) && internalValue.value) {
        close()
      }
    })

    const updatePosition = () => {
      qMenu.value?.updatePosition()

      // Re-run our own position engine, so a reversed menu refreshes its top and max-height when its content changes
      // (Quasar's updatePosition alone leaves it clamped to the first size).
      reposition()
    }

    useResizeObserver(menuResizeObserverRef, updatePosition)

    const getMenuWrapperElement = () => {
      return innerMenuRef.value
    }

    const onShow = async () => {
      if (props.noFocusOnShow) {
        return
      }
      await nextTick()
      if (!innerMenuRef.value) {
        return
      }
      const firstFocusableElement = getFirstFocusableElement(innerMenuRef.value)
      firstFocusableElement?.focus()
    }

    const getTriggerFocusableElement = () => {
      if (props.target) {
        return props.target
      }

      if (!wrapRef.value) {
        return null
      }

      return getFirstFocusableElement(wrapRef.value) || null
    }

    const focusTriggerElement = () => {
      const triggerElement = getTriggerFocusableElement()
      triggerElement?.focus()
    }

    const onMenuKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !innerMenuRef.value) {
        return
      }

      const menuFocusableElements = getFocusableElements(innerMenuRef.value)
      const activeElement = document.activeElement as HTMLElement | null
      const focusedElementIndex = activeElement ? menuFocusableElements.indexOf(activeElement) : -1
      const isBackward = event.shiftKey
      // A menu whose options are navigated with the arrow keys has nothing to tab through,
      // so any Tab leaves it (focusedElementIndex stays -1 and both checks below match).
      const isLeavingMenuForward = !isBackward && focusedElementIndex === menuFocusableElements.length - 1
      const isLeavingMenuBackward = isBackward && focusedElementIndex <= 0

      if (!isLeavingMenuForward && !isLeavingMenuBackward) {
        return
      }

      event.preventDefault()
      close()
      nextTick(focusTriggerElement)
    }

    return {
      dropdownClassName,
      attrs,
      autoClose,
      qMenu,
      wrapRef,
      innerMenuRef,
      menuResizeObserverRef,
      internalValue,
      beforeShow,
      onHide,
      onShow,
      close,
      TestIdAttrName,
      onMenuKeydown,
      // public
      updatePosition,
      getMenuWrapperElement,
      show: () => qMenu.value?.show(),
      toggle: () => {
        internalValue.value = !internalValue.value
      },
    }
  },
})
</script>

<!-- eslint-disable-next-line vue/enforce-style-attribute -->
<style lang="scss">
// Here we have non-scoped style because dropdown appears at the end on body without special uniq attr
// so scoped styles doesn't work
@import '@/styles/mixins.scss';

.tm-dropdown-wrap {
  display: inline-block;

  &--expanded {
    visibility: visible;
  }

  &--hidden {
    display: none;
  }
}
.tm-dropdown {
  @include offset-tooltip();
}
div.tm-dropdown {
  visibility: hidden;
  display: flex !important;
  flex-flow: column;
  overflow: unset !important;
  box-shadow: unset !important;
  background: unset !important;
  border-radius: unset !important;
  width: var(--tm-dropdown-width) !important;

  &--max-height-unset.q-menu {
    max-height: unset !important;
  }
  &__main {
    @include scrollbar-variants();
    position: relative;
    overflow: auto;
    box-shadow: $box-shadow;
    background: $white;
    line-height: 1.5;
    &--border-radius-def {
      border-radius: $border-radius-x;
    }
    &--border-radius-md {
      border-radius: $border-radius-md;
    }
    &--strong-shadow {
      box-shadow: $tooltip-shadow;
    }
    &--centering-shadow {
      box-shadow: $tooltip-centering-shadow;
    }
    &--overflow-hidden {
      display: flex;
      overflow: hidden;

      .tm-dropdown__menu-wrapper {
        display: flex;
        flex-grow: 1;
      }
    }
    &--unset-background {
      background: unset;
    }
  }

  &__menu-wrapper {
    max-width: 100%;
    & > * {
      max-width: 100%;
    }
  }

  &__close {
    position: absolute !important;
    top: 5px !important;
    right: 5px !important;
  }

  .dropdown-list {
    padding: 6px 0;
  }

  &.filter-dropdown {
    min-width: 170px;
  }
  &.small {
    padding: 5px 0;
    .tm-dropdown-item {
      padding: 4px 20px;
      min-height: 25px;
      border-bottom: 0;
      .tm-icon {
        margin-right: 4px;
      }
    }
  }
}
</style>
